import { Link } from 'react-router-dom';
import { Instagram, Youtube } from 'lucide-react';

/** lucide has no TikTok glyph; this is the standard brand path. */
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

const socialLinks = [
  { label: 'Instagram', href: 'https://www.instagram.com/mienviocuba/', Icon: Instagram },
  { label: 'TikTok', href: 'https://www.tiktok.com/@mienviocuba', Icon: TikTokIcon },
  { label: 'YouTube', href: 'https://www.youtube.com/@MiEnvioCuba', Icon: Youtube },
];

/** Light, quiet footer — small type, hairlines, four columns. */
const footerLinks = {
  plataforma: [
    { label: 'Toda la plataforma', path: '/plataforma' },
    { label: 'Mi Dashboard', path: '/plataforma/mi-dashboard' },
    { label: 'Mi Envío Cuba', path: '/plataforma/mi-envio-cuba' },
    { label: 'Mi Admin', path: '/plataforma/mi-admin' },
    { label: 'Mi Cargo · En camino', path: '/plataforma/mi-cargo' },
    { label: 'Mi Restaurante · En camino', path: '/plataforma/mi-restaurante' },
    { label: 'Planes y precios', path: '/plataforma#planes' },
  ],
  agencias: [
    { label: 'Cómo funciona', path: '/como-funciona' },
    { label: 'Para transitarias', path: '/plataforma#transitarias' },
    { label: 'Servicios en detalle', path: '/servicios' },
    { label: 'Socios', path: '/socios' },
    { label: 'Solicita una demo', path: '/plataforma#solicitar' },
  ],
  clientes: [
    { label: 'La app Mi Envío Cuba', path: '/clientes' },
    { label: 'Rastrea tu envío', path: '/rastreo' },
    { label: 'Anti-Fraude', path: '/anti-fraude' },
    { label: 'Artículos prohibidos', path: '/articulos-prohibidos' },
  ],
  empresa: [
    { label: 'Blog', path: '/blog' },
    { label: 'Filantropía', path: '/filantropia' },
    { label: 'Contacto', path: '/contacto' },
  ],
  legal: [
    { label: 'Términos de servicio', path: '/terminos' },
    { label: 'Política de privacidad', path: '/privacidad' },
    { label: 'Guías de la comunidad', path: '/comunidad' },
    { label: 'Derechos de autor (DMCA)', path: '/derechos-autor' },
    { label: 'Política de cookies', path: '/cookies' },
    { label: 'Privacidad estatal', path: '/privacidad-estatal' },
  ],
};

function Column({ title, links }: { title: string; links: { label: string; path: string }[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-text-primary mb-3">{title}</h4>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.path}>
            <Link to={l.path} className="text-xs text-text-secondary hover:text-text-primary transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="w-full bg-bg-light hairline-top">
      <div className="mx-auto w-full max-w-[1120px] px-6 sm:px-8 py-12">
        <p className="text-xs text-text-tertiary max-w-3xl leading-relaxed">
          MEC es una empresa de software. No somos una agencia ni una transitaria: no vendemos, no
          enviamos y no entregamos. Las agencias y transitarias que usan la plataforma son quienes
          reciben, envían y entregan los paquetes. Los tiempos de entrega son estimados que define
          cada agencia.
        </p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-8">
          <Column title="Plataforma" links={footerLinks.plataforma} />
          <Column title="Agencias" links={footerLinks.agencias} />
          <Column title="Clientes" links={footerLinks.clientes} />
          <Column title="Empresa" links={footerLinks.empresa} />
          <Column title="Legal" links={footerLinks.legal} />
        </div>

        <div className="mt-10 pt-6 hairline-top flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-text-tertiary">
          <div className="flex items-center gap-2">
            <img src="/logo-96.png" alt="" width="20" height="20" className="w-5 h-5 object-contain" />
            <span>© Mi Envío Cuba. Todos los derechos reservados.</span>
          </div>
          <div className="flex items-center gap-4">
            <p>25 SW 9th St, Suite 509 · Miami, FL 33130 · support@mienviocuba.com</p>
            <span aria-hidden="true" className="hidden sm:inline">·</span>
            <div className="flex items-center gap-3">
              {socialLinks.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Mi Envío Cuba en ${label}`}
                  className="text-text-tertiary hover:text-text-primary transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
